/**
 * Upload-time warning for a cast photo that shows more than one person.
 *
 * The Director casts ONE person per uploaded photo: the brief's vision
 * profile describes the photo's most prominent person, the storyboard's face
 * crop anchors that same face, and extra_character_N binds to upload N.
 * Everyone else in the photo silently never appears. CwWtQQYY8Fw58t-NUqbRq
 * (Sep 28 2026, iOS smart flow): the extra photo showed two men, the bald
 * one was cast in every scene and his friend never showed up ("Cant
 * recognize 1 person"). The mobile "Add more people" upload never ran the
 * photo classifier, so nothing warned before the user paid.
 *
 * Composers warn at upload, before any credits are spent, from
 * director.classifyReferenceImage's subjectCount (people who are clearly the
 * photo's subject; background crowds do not count). The advice respects the
 * surface's slot limit: telling someone with a five-person band photo and
 * two free slots to "upload one photo per person" sends them into a wall.
 *
 * Pure, so the web composers and the mobile app share one rule; the English
 * copy lives here too (mobile passes it to t() as the defaultValue).
 */

export type MultiPersonCastPhotoAdvice =
  /** Everyone in the photo fits: one photo per person. */
  | "one_photo_each"
  /** Only `room` of them fit, one photo each. */
  | "pick_some"
  /** No room for anyone else: use a photo of just the one person. */
  | "single_person";

export type MultiPersonCastPhotoWarning = {
  /** People the classifier saw as the photo's subject (2 or more). */
  count: number;
  /**
   * How many of them the surface can still cast with one photo each: this
   * photo's own slot plus the free ones, capped at `count`.
   */
  room: number;
  advice: MultiPersonCastPhotoAdvice;
};

/**
 * The warning to show for a just-added cast photo, or null when the photo
 * shows one person or the classifier could not tell (null subjectCount —
 * never warn on a guess).
 *
 * `freeSlots`: people photos the surface still accepts besides this one
 * (0 on a single-photo singing video or a full composer).
 */
export function multiPersonCastPhotoWarning(input: {
  subjectCount: unknown;
  freeSlots: number;
}): MultiPersonCastPhotoWarning | null {
  const { subjectCount } = input;
  if (typeof subjectCount !== "number" || !Number.isFinite(subjectCount)) {
    return null;
  }
  const count = Math.round(subjectCount);
  if (count < 2) return null;
  const freeSlots = Number.isFinite(input.freeSlots)
    ? Math.max(0, Math.floor(input.freeSlots))
    : 0;
  const room = Math.min(count, 1 + freeSlots);
  const advice: MultiPersonCastPhotoAdvice =
    room >= count ? "one_photo_each" : room >= 2 ? "pick_some" : "single_person";
  return { count, room, advice };
}

/** English templates; `{{num}}` = people in the photo, `{{room}}` = how many fit. */
export const MULTI_PERSON_CAST_PHOTO_COPY = {
  title: "This photo shows {{num}} people",
  one_photo_each:
    "Only one of them will be in your video. Upload one photo per person to include everyone.",
  pick_some:
    "Only one of them will be in your video. You can add up to {{room}} of them, one photo each.",
  single_person:
    "Only one of them will be in your video. Use a photo of just the person you want.",
} as const;

function fill(template: string, warning: MultiPersonCastPhotoWarning): string {
  return template
    .replace(/\{\{num\}\}/g, String(warning.count))
    .replace(/\{\{room\}\}/g, String(warning.room));
}

/** The warning's English title and body. */
export function multiPersonCastPhotoCopy(warning: MultiPersonCastPhotoWarning): {
  title: string;
  body: string;
} {
  return {
    title: fill(MULTI_PERSON_CAST_PHOTO_COPY.title, warning),
    body: fill(MULTI_PERSON_CAST_PHOTO_COPY[warning.advice], warning),
  };
}

/**
 * A button of the mobile warning alert:
 * - "keep": close the alert, the photo stays as it is.
 * - "remove": take the photo out of the cast.
 * - "keep_one": split the photo and pick the one person to keep.
 * - "split": the warning's split action (see castPhotoSplitActionLabel).
 */
export type MultiPersonCastPhotoChoice = "keep" | "remove" | "keep_one" | "split";

/**
 * The warning alert's buttons, in order. A cast that takes one person per
 * photo has no "keep": a kept group photo casts whoever the pipeline finds
 * most prominent, and people keep it without knowing who that will be.
 * 9GZ48gug6I4DfLvD8afJw (Sep 30 2026, Android): the extra photo was a man
 * and a woman singing, the user closed the warning three times, and the
 * woman was cast, so the video had two women and no man. The user now
 * chooses: remove it, keep one person they pick, or split it.
 * With room for one person the split action already is the one-person pick,
 * so "keep_one" is left out there. A single-photo singing or dance video
 * animates the photo itself, so it keeps "keep".
 */
export function multiPersonCastPhotoChoices(
  warning: MultiPersonCastPhotoWarning,
  castsOnePersonPerPhoto: boolean,
): MultiPersonCastPhotoChoice[] {
  if (!castsOnePersonPerPhoto) return ["keep", "split"];
  return warning.advice === "single_person"
    ? ["remove", "split"]
    : ["remove", "keep_one", "split"];
}

/*
 * "Split into N photos": the warning's one-tap fix. The server
 * (director.splitCastPhotoByPerson) crops each featured person into their
 * own head-and-shoulders photo, left to right; the composer swaps the group
 * photo for those crops, so the pipeline still gets one person per photo.
 * When not everyone fits the surface's slots, the user picks who.
 */

/** One person cropped out of a cast photo; `position` 1 = leftmost. */
export type CastPhotoPersonCrop = { url: string; position: number };

/**
 * What the split does with the crops of a photo that has `room` cast slots
 * (its own plus the free ones):
 * - "add_all": everyone fits, the crops replace the photo right away.
 * - "pick": more people than slots, the user picks up to `max` of them.
 * - "none": fewer than two people were told apart, the photo stays.
 */
export type CastPhotoSplitPlan =
  | { kind: "add_all"; crops: CastPhotoPersonCrop[] }
  | { kind: "pick"; crops: CastPhotoPersonCrop[]; max: number }
  | { kind: "none" };

export function castPhotoSplitPlan(
  crops: readonly CastPhotoPersonCrop[] | null | undefined,
  room: number,
): CastPhotoSplitPlan {
  const seen = new Set<string>();
  const ordered: CastPhotoPersonCrop[] = [];
  for (const crop of crops ?? []) {
    if (typeof crop?.url !== "string" || !crop.url || seen.has(crop.url)) {
      continue;
    }
    seen.add(crop.url);
    ordered.push(crop);
  }
  ordered.sort((a, b) => a.position - b.position);
  if (ordered.length < 2) return { kind: "none" };
  const max = Number.isFinite(room) ? Math.max(1, Math.floor(room)) : 1;
  return ordered.length <= max
    ? { kind: "add_all", crops: ordered }
    : { kind: "pick", crops: ordered, max };
}

/**
 * The pick after tapping the crop at `position`: a picked crop is dropped;
 * with room for one person a new tap replaces the pick; otherwise a tap
 * past `max` is ignored.
 */
export function toggleCastPhotoSplitPick(
  picked: readonly number[],
  position: number,
  max: number,
): number[] {
  if (picked.includes(position)) return picked.filter((p) => p !== position);
  if (max <= 1) return [position];
  return picked.length >= max ? [...picked] : [...picked, position];
}

/**
 * `list` with the photo at `index` replaced by the first crop and the other
 * crops appended, never past `maxLength` photos. Every other photo keeps
 * its place, so an "@imageN" or "Person N" the user already wrote still
 * means the same photo, and the split photo's own place (the lead, when it
 * led) goes to its first person.
 */
export function spliceCastPhotoSplit<T>(
  list: readonly T[],
  index: number,
  crops: readonly T[],
  maxLength: number,
): T[] {
  if (index < 0 || index >= list.length || crops.length === 0) return [...list];
  const next = [...list];
  next[index] = crops[0];
  const free = Math.max(0, Math.floor(maxLength) - next.length);
  return [...next, ...crops.slice(1, 1 + free)];
}

/**
 * Cast slots a split photo can fill: its own plus the free ones (at least 1,
 * the photo's own). `photos` counts every photo on the surface, this one
 * included; `maxPhotos` is the surface's limit (web picker MAX_REFERENCES,
 * mobile main photo + MAX_ADDITIONAL_CHARACTER_PHOTOS).
 */
export function castPhotoSplitRoom(photos: number, maxPhotos: number): number {
  const room = Math.floor(maxPhotos) - Math.floor(photos) + 1;
  return Number.isFinite(room) ? Math.max(1, room) : 1;
}

/**
 * A lead-plus-extras cast (mobile narrative: the main photo, "" when none is
 * picked and the first extra leads, plus up to `maxExtras` extra people)
 * after `photo` is split into `crops`. A cast without a main photo stays
 * that way while the extras hold everyone; otherwise the first person leads.
 * Null when `photo` is not in the cast.
 */
export function splitLeadAndExtras(input: {
  lead: string;
  extras: readonly string[];
  maxExtras: number;
  photo: string;
  crops: readonly string[];
}): { lead: string; extras: string[] } | null {
  const people = input.lead ? [input.lead, ...input.extras] : [...input.extras];
  const index = people.indexOf(input.photo);
  if (index < 0) return null;
  const maxExtras = Math.max(0, Math.floor(input.maxExtras));
  const next = spliceCastPhotoSplit(people, index, input.crops, 1 + maxExtras);
  if (!input.lead && next.length <= maxExtras) {
    return { lead: "", extras: next };
  }
  return { lead: next[0] ?? "", extras: next.slice(1) };
}

/*
 * Who leads after a split. The split photo's first person takes its place,
 * so splitting the photo that leads the cast makes its LEFTMOST person the
 * main character. Sep 30 2026 (iOS): a couple photo, she stood on the left,
 * he sang the song; "Split into 2 photos" made her the lead with no way to
 * swap. When the split decides the lead, the user taps who leads instead.
 */

/**
 * True when splitting `photo` decides who leads a lead-plus-extras cast
 * (the splitLeadAndExtras input): it is the main photo, or, with no main
 * photo picked, the first extra, which leads the project. Its first person
 * becomes the main character either way.
 */
export function castPhotoSplitDecidesLead(input: {
  lead: string;
  extras: readonly string[];
  photo: string;
}): boolean {
  if (!input.photo) return false;
  return (input.lead || input.extras[0] || "") === input.photo;
}

/**
 * The crops in the order the split applies them: the person at
 * `leadPosition` first (they take the split photo's slot, so they lead when
 * it led), everyone else left to right. An unknown position keeps plain
 * left-to-right order.
 */
export function castPhotoSplitLeadFirst<T extends CastPhotoPersonCrop>(
  crops: readonly T[],
  leadPosition: number,
): T[] {
  const byPosition = [...crops].sort((a, b) => a.position - b.position);
  const lead = byPosition.find((crop) => crop.position === leadPosition);
  if (!lead) return byPosition;
  return [lead, ...byPosition.filter((crop) => crop !== lead)];
}

/**
 * Storage key of person `index` cropped by split `splitId`. Every crop of
 * one split shares the id, which is how the pipeline knows two cast photos
 * are different people cut from the same group photo.
 */
export function castPhotoSplitKey(splitId: string, index: number): string {
  return `ai-music-video/images/person-${splitId}-${index}.png`;
}

const SPLIT_KEY_RE =
  /\/ai-music-video\/images\/person-([A-Za-z0-9_-]{8,40})-(\d{1,2})\.png(?:$|[?#])/;

/** The split a cast photo was cropped by, or null for any other photo. */
export function castPhotoSplitId(
  url: string | null | undefined,
): string | null {
  if (typeof url !== "string") return null;
  return SPLIT_KEY_RE.exec(url)?.[1] ?? null;
}

/**
 * True for two different crops of one split: different people by
 * construction, however alike the shared lighting and background make them
 * look to a same-person check.
 */
export function castPhotoSplitSiblings(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const id = castPhotoSplitId(a);
  return id !== null && a !== b && id === castPhotoSplitId(b);
}

/** English copy of the split action; mobile passes it to t() as defaults. */
export const CAST_PHOTO_SPLIT_COPY = {
  /** Warning action when everyone fits: `{{num}}` = people in the photo. */
  one_photo_each: "Split into {{num}} photos",
  /** Warning action when only some of them fit. */
  pick_some: "Pick who to add",
  /** Warning action when only one of them fits. */
  single_person: "Pick who to keep",
  splitting: "Finding each person…",
  pickTitle: "Who should be in your video?",
  /** `{{max}}` = how many can be picked. */
  pickHint: "Pick up to {{max}}.",
  pickHintOne: "Pick one.",
  /** Split of the photo that leads the cast: who takes the lead slot. */
  leadTitle: "Who is the main character?",
  leadHint: "Tap the person who leads your video. Everyone else is added too.",
  done: "Done",
  cancel: "Cancel",
  failed:
    "Couldn't tell the people in this photo apart. Upload one photo per person instead.",
} as const;

/** The warning's split action label, in English. */
export function castPhotoSplitActionLabel(
  warning: MultiPersonCastPhotoWarning,
): string {
  return fill(CAST_PHOTO_SPLIT_COPY[warning.advice], warning);
}
